import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import PublicLayout, { SEOHead, CTASection } from '../../components/PublicLayout';
import { Clock, ArrowLeft, Tag, Share2 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const CLUSTER_LABELS = {
  'service-explainers': 'Service Explainers',
  'industry-specific': 'Industry Insights',
  'immigration-uscis': 'Immigration & USCIS',
  'compliance': 'Language Access & Compliance',
  'buyer-guides': 'Buyer Guides & Comparisons',
  'use-cases': 'Use Cases & How-To',
};

function renderMarkdown(content) {
  if (!content) return '';
  return content
    .replace(/^### (.+)$/gm, '<h3 class="text-xl font-bold mt-8 mb-3 text-slate-900">$1</h3>')
    .replace(/^## (.+)$/gm, '<h2 class="text-2xl font-bold mt-10 mb-4 text-slate-900">$1</h2>')
    .replace(/^# (.+)$/gm, '<h1 class="text-3xl font-bold mt-10 mb-4 text-slate-900">$1</h1>')
    .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
    .replace(/\*(.+?)\*/g, '<em>$1</em>')
    .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" class="text-blue-700 font-semibold hover:underline">$1</a>')
    .replace(/^- (.+)$/gm, '<li class="ml-4 mb-1">$1</li>')
    .replace(/(<li.*<\/li>\n?)+/g, (m) => `<ul class="list-disc pl-4 my-4 text-slate-700 leading-relaxed">${m}</ul>`)
    .replace(/^(?!<[hulo]|<li|<strong|<em|<a|\s*$)(.+)$/gm, '<p class="text-slate-700 leading-relaxed mb-4">$1</p>')
    .replace(/\n{2,}/g, '');
}

export default function BlogPostPage() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    fetch(`${API}/api/blog/posts/${slug}`)
      .then(r => { if (!r.ok) throw new Error(); return r.json(); })
      .then(setPost)
      .catch(() => setNotFound(true));
  }, [slug]);

  if (notFound) return <PublicLayout><div className="py-20 text-center"><h1 className="text-2xl font-bold mb-4">Article Not Found</h1><Link to="/blog" className="text-blue-700 hover:underline">Back to Blog</Link></div></PublicLayout>;
  if (!post) return <PublicLayout><div className="py-20 text-center text-slate-500">Loading...</div></PublicLayout>;

  const date = post.published_at ? new Date(post.published_at).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }) : '';
  const canonical = `https://interpreterservices.us/blog/${post.slug}`;

  const schemas = [];
  schemas.push({
    "@context": "https://schema.org",
    "@type": "Article",
    headline: post.title,
    description: post.meta_description || post.excerpt,
    author: { "@type": "Organization", name: "Interpreter Services" },
    publisher: { "@type": "Organization", name: "Interpreter Services", url: "https://interpreterservices.us" },
    datePublished: post.published_at,
    dateModified: post.updated_at,
    mainEntityOfPage: canonical,
  });
  schemas.push({
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: "https://interpreterservices.us/" },
      { "@type": "ListItem", position: 2, name: "Blog", item: "https://interpreterservices.us/blog" },
      { "@type": "ListItem", position: 3, name: post.title, item: canonical },
    ],
  });
  if (post.faq && post.faq.length > 0) {
    schemas.push({
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: post.faq.map(f => ({
        "@type": "Question",
        name: f.q,
        acceptedAnswer: { "@type": "Answer", text: f.a },
      })),
    });
  }

  return (
    <PublicLayout>
      <SEOHead
        title={`${post.meta_title || post.title} | Interpreter Services`}
        description={post.meta_description || post.excerpt}
        canonical={canonical}
        schema={schemas.length > 1 ? { "@context": "https://schema.org", "@graph": schemas } : schemas[0]}
      />

      <article className="max-w-4xl mx-auto px-6 py-12" data-testid="blog-post">
        <nav className="mb-6 text-sm text-slate-500" aria-label="Breadcrumb">
          <Link to="/" className="hover:text-blue-700">Home</Link>
          <span className="mx-2">/</span>
          <Link to="/blog" className="hover:text-blue-700">Blog</Link>
          <span className="mx-2">/</span>
          <span className="text-slate-900">{post.title}</span>
        </nav>

        {post.cluster && (
          <Link to={`/blog?cluster=${post.cluster}`} className="text-xs font-medium text-blue-700 uppercase tracking-wide hover:underline">
            {CLUSTER_LABELS[post.cluster] || post.cluster}
          </Link>
        )}

        <h1 className="text-3xl sm:text-4xl font-bold text-slate-900 mt-2 mb-4" data-testid="blog-post-title">{post.title}</h1>

        <div className="flex items-center gap-4 text-sm text-slate-500 mb-8">
          {date && <span className="flex items-center gap-1"><Clock className="w-4 h-4" />{date}</span>}
          {post.author && <span>By {post.author}</span>}
        </div>

        {post.featured_image && (
          <img src={post.featured_image} alt={post.title} className="w-full rounded-xl mb-10 object-cover max-h-96" loading="eager" />
        )}

        {/* Article content */}
        <div className="prose prose-slate max-w-none" dangerouslySetInnerHTML={{ __html: renderMarkdown(post.content) }} data-testid="blog-post-content" />

        {/* FAQ section */}
        {post.faq && post.faq.length > 0 && (
          <section className="mt-12 mb-8">
            <h2 className="text-2xl font-bold mb-6">Frequently Asked Questions</h2>
            <div className="space-y-5">
              {post.faq.map((f, i) => (
                <div key={i} className="border-b pb-4">
                  <h3 className="font-bold text-slate-900 mb-2">{f.q}</h3>
                  <p className="text-sm text-slate-600">{f.a}</p>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Tags */}
        {post.tags && post.tags.length > 0 && (
          <div className="flex flex-wrap gap-2 mt-8 pt-6 border-t">
            <Tag className="w-4 h-4 text-slate-400 mt-0.5" />
            {post.tags.map(t => (
              <Link key={t} to={`/blog?tag=${t}`} className="px-3 py-1 bg-slate-100 text-slate-700 rounded-lg text-sm hover:bg-blue-50 hover:text-blue-700 transition-colors">{t}</Link>
            ))}
          </div>
        )}

        {/* Back to blog */}
        <div className="mt-10">
          <Link to="/blog" className="flex items-center gap-2 text-blue-700 font-semibold hover:underline" data-testid="blog-back">
            <ArrowLeft className="w-4 h-4" />Back to Blog
          </Link>
        </div>
      </article>

      <CTASection />
    </PublicLayout>
  );
}
